import _ from 'underscore';
import { ImageOData } from '@bingads-webui-campaign/image-odata';
import { queryify } from '@bingads-webui/url-util';
import { getAdCreatorInstrumentation } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { deserializeImageAssetMetadata } from '@bingads-webui-campaign/media-metadata-model';
import { getExpandedImages, updatePlacementWithBaseCropSettings, updatePlacementWithPlacementSettings } from '@bingads-webui-campaign/image-experience-utils';

import C from '../constants';
import {
  validateImageSizeAsync,
  validateImageWithDimension,
} from './image-size-validation';
import { newImageFromMTObject } from '../model/image-experience-image';
import { getSmartCroppedImagesAsync } from './get-smart-cropped-image';

export class ImageRepository {
  constructor(cid, aid, oDataConfig, imageExperienceConfig) {
    // Internal implementations are migrated to imageOData.
    // Still keep this layer so no need to change places consuming this code
    this.imageOData = new ImageOData(cid, aid, oDataConfig);

    this.imageExperienceConfig = imageExperienceConfig;
    this.validateImageWithDimension = validateImageWithDimension;
    this.getAdCreatorInstrumentation = getAdCreatorInstrumentation;
    this.validateImageSizeAsync = validateImageSizeAsync;
  }

  /**
   * Gets previously uploaded images (20).
   * @param {number} sortById - id for the sort criteria
   * @param {string} searchTerm - search term for query
   * @return {Promise} Response from the oData call.
   */
  getExistingImages({
    sortById,
    searchTerm,
    pageNumber,
    pageSize,
    filterTerm = '',
  }) {
    const sortDefinition = _.find(
      C.sortBySelections,
      sortBySelection => sortBySelection.id === sortById
    );
    const sortQuery = sortDefinition && sortDefinition.query;

    return this.imageOData.getExistingImages({
      sortQuery,
      searchTerm,
      pageNumber,
      pageSize,
      filterTerm,
    }).then((response) => {
      _.each(response.value, (item) => {
        // &c=0 means no crop. It wil help convert tiff to jpg so that img tag can be displayed.
        // This is to mitigate unexpected MMA demand associated with tiff images caused by a MT bug
        // Attention: this is not an official support for tiff format.
        // If we want to officially support a new image type, need more changes/tests.
        _.extend(item, {
          Url: queryify(item.Url, { c: 0 }),
        });
      });

      return response;
    });
  }

  /**
   * Gets image specified by the id.
   * @param {number} imageId Id of the image to be requested.
   * @param {boolean} isImageFittingEnabled - is image fitting enabled or not
   * @param {string} adType - for different adType, MT returns image fittings with different assets ratios
   * @return {Promise} Response from the oData call.
   */
  getImageByIdAsync(imageId, isImageFittingEnabled, adType) {
    return this.imageOData.getImageByIdAsync(imageId, isImageFittingEnabled, adType);
  }

  getImageByIdAndProcessAsync({
    image,
    imageType,
    imageSource,
    onError,
    imageOption,
    aspectRatioOption,
    onSelectedImagesSet,
    optionalCallback,
    isImageFittingEnabled,
    adType,
  }) {
    const { id, suggestionId } = image || {};
    if (id) {
      return this.getImageByIdAsync(id, isImageFittingEnabled, adType)
        .then(imgObjMT => this.processNewlyAddedImage({
          imgObjMT,
          imageSource,
          imageType,
          imageOption,
          aspectRatioOption,
          onSelectedImagesSet,
          optionalCallback,
          isImageFittingEnabled,
        }))
        .then((processedImage) => {
          if (suggestionId) {
            return { ...processedImage, suggestionId };
          }
          return processedImage;
        })
        .catch(e => onError(e));
    }
    return Promise.resolve();
  }

  getImagesByIdsAndProcessAsync({
    imageIds,
    suggestionIds = [],
    imageType,
    imageSource,
    onError,
    imageOption,
    aspectRatioOption,
    onSelectedImagesSet,
    optionalCallback,
    isImageFittingEnabled,
    adType,
  }) {
    const addImagePromises = [];

    _.each(imageIds, (imageId, index) => {
      addImagePromises.push(this.getImageByIdAndProcessAsync({
        image: { id: imageId, suggestionId: suggestionIds[index] },
        imageType,
        imageSource,
        onError,
        imageOption,
        aspectRatioOption,
        onSelectedImagesSet,
        optionalCallback,
        isImageFittingEnabled,
        adType,
      }));
    });

    return Promise.all(addImagePromises);
  }

  processNewlyAddedImage({
    imgObjMT,
    imageSource,
    imageType,
    imageOption,
    aspectRatioOption,
    onSelectedImagesSet,
    optionalCallback,
    isImageFittingEnabled,
  }) {
    const {
      isAudienceAd,
      isMultimediaAd,
      instrumentation,
      mediaRepository,
      isMultimediaAdinMSANEnabled,
      isImageAdExtensionMode,
      isCombinedMsanAndMmaAspectRatios,
      isMMAV2Enabled,
      adCreatorScenario,
      isMSANBoostEnabled,
      isBoost200x100ImageEnabled,
      isBoostNewAspectRatioSupportEnabled,
      isAudienceAdSmartCropEnabled,
      isPmaxSmartCropEnabled,
      isStoreAppProng,
      isStoreAppSpotLight,
      isStoreAppTransparentLogo,
      isUnifiedAppCampaignWindowsStore,
      isLinkedInAd,
      isPMaxAspectRatiosV2Enabled,
    } = this.imageExperienceConfig;

    const postValidationError =
      this.validateImageWithDimension(
        imgObjMT.Width,
        imgObjMT.Height,
        imageType,
        isMultimediaAd || isCombinedMsanAndMmaAspectRatios,
        imageOption,
        isMMAV2Enabled,
        false, /* isCombinedMsanAndMmaAspectRatios */
        false, /* isDisplayAds */
        false, /* isPortraitImage */
        isStoreAppProng,
        isStoreAppSpotLight,
        isStoreAppTransparentLogo,
        isUnifiedAppCampaignWindowsStore,
        isLinkedInAd,
        isPMaxAspectRatiosV2Enabled
      );

    /* istanbul ignore else */
    if (postValidationError) {
      postValidationError.isDueToResizing = true;

      throw postValidationError;
    }

    optionalCallback && optionalCallback();

    const { smartCropActivity } =
      this.getAdCreatorInstrumentation(instrumentation, adCreatorScenario);

    let resolveOriginalImagePromise = Promise.resolve(imgObjMT);

    const hasBaseImage = imgObjMT.BaseIdString &&
      imgObjMT.BaseIdString !== imgObjMT.IdString;
    const hasBeenBulkCropped = imgObjMT.OriginalUrl &&
      imgObjMT.OriginalUrl !== imgObjMT.Url;
    const hasPlacementSettings = imgObjMT.PlacementSettings &&
      imgObjMT.PlacementSettings.length > 0;

    const { effectSettings } = hasBaseImage ? (deserializeImageAssetMetadata(imgObjMT.Metadata) || {}) : {};

    if (hasBaseImage && effectSettings) {
      const originalImage = imgObjMT.BaseIdString;

      resolveOriginalImagePromise =
        resolveOriginalImagePromise.then(preprocessImgObjMT =>
          this.getImageByIdAsync(originalImage, isImageFittingEnabled)
            .then((baseImgMT) => {
              // for smart effects, use full original image regardless of crop settings
              const originalUrl =
                baseImgMT.OriginalUrl || baseImgMT.Url;

              return _.extend({}, preprocessImgObjMT, {
                Id: Number(imgObjMT.BaseIdString),
                IdString: imgObjMT.BaseIdString,
                Url: originalUrl,
              });
            }));
    }

    return resolveOriginalImagePromise
      .then((finalImgObjMT) => {
        const imgObj = newImageFromMTObject(finalImgObjMT, imageSource);
        // if image fitting pilot is not enabled,
        //   or there are manual cropping on image level
        //   or there are smart effects for the image
        // or ROIInfo is null
        // don't use image fitting
        if (!isImageFittingEnabled || hasBeenBulkCropped || effectSettings || imageOption !== C.defaultImageOption || !finalImgObjMT.ROIInfo) {
          return { imgObj, skipSmartCrop: false };
        }

        const imageObjExpanded = getExpandedImages({
          originalImageToExpand: imgObj,
          isMultimediaAd,
          isMultimediaAdinMSANEnabled,
          isCombinedMsanAndMmaAspectRatios,
          isMMAV2Enabled,
          isBoost200x100ImageEnabled,
          isBoostNewAspectRatioSupportEnabled,
          isPMaxAspectRatiosV2Enabled,
          logger: smartCropActivity,
        });

        if (imageObjExpanded) {
          return { imgObj, imageObjExpanded, skipSmartCrop: true };
        }
        return { imgObj, skipSmartCrop: false };
      })
      .then(({ imgObj, imageObjExpanded, skipSmartCrop }) => {
        if (skipSmartCrop) {
          return { imgObj, imgObjCropped: imageObjExpanded };
        }
        return getSmartCroppedImagesAsync({
          mediaRepository,
          isAudienceAd,
          originalImageToCrop: imgObj,
          smartCropActivity,
          isMultimediaAd,
          isMultimediaAdinMSANEnabled,
          imageOption,
          aspectRatioOption,
          isImageAdExtensionMode,
          isCombinedMsanAndMmaAspectRatios,
          isMMAV2Enabled,
          isMSANBoostEnabled,
          isBoost200x100ImageEnabled,
          isBoostNewAspectRatioSupportEnabled,
          isAudienceAdSmartCropEnabled,
          isPmaxSmartCropEnabled,
          isStoreAppProng,
          isStoreAppSpotLight,
          isStoreAppTransparentLogo,
          isUnifiedAppCampaignWindowsStore,
          isLinkedInAd,
          setCroppingType: isImageAdExtensionMode || isImageFittingEnabled,
          isPMaxAspectRatiosV2Enabled,
        }).then(imgObjCropped => ({ imgObj, imgObjCropped }));
      }).then(({ imgObj, imgObjCropped }) => {
        if (hasBaseImage && effectSettings) {
          const effectSettingsModel = _.defaults({ effectSrc: imgObjMT.Url }, effectSettings);

          _.each(imgObjCropped, (placement) => {
            placement.BaseImage.setEffectSettings(effectSettingsModel);
            placement.setEffectSettings(effectSettingsModel);
          });
        }

        if (hasBeenBulkCropped) {
          _.each(imgObjCropped, (placement) => {
            updatePlacementWithBaseCropSettings({
              placement,
              mtImgObject: imgObjMT,
              setCroppingType: isImageAdExtensionMode || isImageFittingEnabled,
            });
          });
        }

        if (hasPlacementSettings) {
          _.each(imgObjCropped, (placement) => {
            updatePlacementWithPlacementSettings({
              isMultimediaAd,
              isMultimediaAdinMSANEnabled,
              isCombinedMsanAndMmaAspectRatios,
              isPMaxAspectRatiosV2Enabled,
              placement,
              mtImgObject: imgObjMT,
              updatePlacementSettings: true,
              setCroppingType: isImageAdExtensionMode || isImageFittingEnabled,
            });
          });
        }

        if (_.isArray(imgObjCropped) && imgObjCropped.length === 1 && (!isStoreAppProng && !isStoreAppSpotLight && !isStoreAppTransparentLogo)) {
          if (imgObjCropped[0].isPortrait) {
            // we have only one placement for portrait images
            onSelectedImagesSet(imgObjCropped);
          } else {
            onSelectedImagesSet(imgObjCropped[0]);
          }
        } else {
          onSelectedImagesSet(imgObjCropped);
        }

        return Promise.resolve(imgObj);
      });
  }

  uploadImageAsync(imageData, activityLogger = null, inProgress = null, imageOption = C.defaultImageOption) { // eslint-disable-line max-len
    return this.imageOData.uploadImageAsync(imageData, activityLogger, inProgress, imageOption === C.imageOptionKeys.Logo); // eslint-disable-line max-len
  }

  uploadImageWithValidationAsync({
    imageData,
    updateCurrentProgress,
    name,
    imageOption,
    imageType,
  }) {
    const { isMultimediaAd } = this.imageExperienceConfig;

    return this.validateImageSizeAsync(imageData, imageType, isMultimediaAd, imageOption)
      // eslint-disable-next-line arrow-body-style
      .then(() => {
        return this.uploadImageAsync(
          imageData,
          null,
          updateCurrentProgress,
          C.defaultImageOption,
          name
        )
          .then((response) => {
            if (!response || !response.imageId) {
              throw new Error(response && response[0] && response[0].Code);
            }
            return response;
          });
      });
  }

  updateImageAsync(imageData) {
    return this.imageOData.updateImageAsync(imageData);
  }

  deleteImageAsync(imageData) {
    return this.imageOData.deleteImageAsync(imageData);
  }

  bulkSaveImagesByUrl(imageUrls) {
    return this.imageOData.bulkSaveImagesByUrl(imageUrls);
  }

  addAssetsByUrl(images) {
    const addAssetPromises = _.map(images, image => this.imageOData.addAsset(image).catch(err => err));

    return Promise.allSettled(addAssetPromises).then((results) => {
      const allErrors = _.filter(results, result => result.status === 'rejected').map(result => result.reason);
      // only throw error if all images failed
      if (allErrors.length === results.length) {
        const firstErrorCode = allErrors[0] && allErrors[0].errors && allErrors[0].errors[0] && allErrors[0].errors[0].Code;
        throw new Error(firstErrorCode);
      } else {
        return _.filter(results, result => result.status === 'fulfilled').map(result => result.value);
      }
    });
  }

  addImageByUrlThenProcessAsync({
    imageUrl,
    imageType,
    imageSource = C.imageSources.WebsiteCarousel,
    onError,
    imageOption,
    aspectRatioOption,
    onSelectedImagesSet,
    suggestionId,
    isImageFittingEnabled,
    adType,
  }) {
    const {
      isMultimediaAd,
    } = this.imageExperienceConfig;

    const sourceType = C.getSourceTypeFromImageSource(imageSource);
    return this.validateImageSizeAsync(imageUrl, null, isMultimediaAd, imageOption).bind(this)
      .then(() => {
        if (isImageFittingEnabled) {
          return this.addAssetsByUrl([{ url: imageUrl, sourceType }])
            .then(ids => ({ id: ids[0], suggestionId }));
        }
        return this.bulkSaveImagesByUrl([{ url: imageUrl, sourceType }])
          .then((ids) => {
            if (_.isObject(_.first(ids))) {
              throw new Error(ids[0].code);
            }

            return { id: ids[0], suggestionId };
          })
      })
      .then(image => this.getImageByIdAndProcessAsync({
        image,
        imageType,
        imageSource,
        onError,
        imageOption,
        aspectRatioOption,
        onSelectedImagesSet,
        isImageFittingEnabled,
        adType,
      }))
      .then(processedImage => ({ processedImage }))
      .catch(e => onError(e));
  }

  addImagesByUrlThenProcessAsync({
    images,
    imageType,
    imageSource = C.imageSources.WebsiteCarousel,
    imageOption,
    onError,
    aspectRatioOption,
    onSelectedImagesSet,
    isImageFittingEnabled,
  }) {
    const {
      isMultimediaAd,
    } = this.imageExperienceConfig;

    const sourceType = C.getSourceTypeFromImageSource(imageSource);
    const validateImageSizePromises = [];

    let errors = [];

    _.each(images, ({ fullUrl }) => {
      validateImageSizePromises.push(this.validateImageSizeAsync(fullUrl, null, isMultimediaAd, imageOption).bind(this).catch((e) => {
        errors = errors.concat(e.message);
        return e.message;
      }));
    });

    return Promise.all(validateImageSizePromises)
      .then((values) => {
        let index = 0;
        return _.reduce(images, (memo, { fullUrl, suggestionId }) => {
          if (!values[index]) { // if no error, promise resolved with undefined
            memo.push({ url: fullUrl, sourceType, suggestionId });
          }
          index += 1;
          return memo;
        }, []);
      })
      .then((validateImages) => {
        const suggestionIds = _.pluck(validateImages, 'suggestionId');
        if (isImageFittingEnabled) {
          return this.addAssetsByUrl(validateImages)
            .then((ids) => {
              const imageIdsAndSuggestionIds = ids.map((id, index) => ({
                imageId: id,
                suggestionId: suggestionIds[index],
              }));
              return imageIdsAndSuggestionIds;
            });
        }
        return this.bulkSaveImagesByUrl(validateImages)
          .then((ids) => {
            const imageIdsAndSuggestionIds = ids.map((id, index) => ({
              imageId: id,
              suggestionId: suggestionIds[index],
            }));
            const saveErrors = _.chain(ids)
              .filter(id => typeof id === 'object')
              .map(id => id.code)
              .value();

            errors = errors.concat(saveErrors);

            const filteredImageIds = _.chain(ids)
              .filter(id => typeof id === 'string')
              .value();

            return imageIdsAndSuggestionIds.filter(id => filteredImageIds.includes(id.imageId));
          })
      })
      .then(imageIdsAndSuggestionIds => (
        this.getImagesByIdsAndProcessAsync({
          imageIds: _.pluck(imageIdsAndSuggestionIds, 'imageId'),
          suggestionIds: _.pluck(imageIdsAndSuggestionIds, 'suggestionId'),
          imageType,
          imageSource,
          onError: (e) => { errors = errors.concat(e.message); },
          imageOption,
          aspectRatioOption,
          onSelectedImagesSet,
          isImageFittingEnabled,
        })
      ))
      .then(processedImages =>
        ({ ...onError(errors), processedImages })) // Check with Yen + Lingling)
      .catch((e) => {
        errors = errors.concat(e.message);
        return onError(errors);
      });
  }
}
